import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import DateConverter from "../../../utils/DateConverter";
import {
  PrimaryColor,
  TableHeaderGrey,
  TableBodyGrey,
  TableRowHover,
} from "../../../shared/styles/colors";
import { Typography, Button } from "@material-ui/core";
import { TablePagination } from "@mui/material";
import { ToastContainer } from "react-toastify";
import { styled } from "@mui/material/styles";
import { Spinner, ContractorModal, ContractorDetailModal } from "../../index";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CachedIcon from "@mui/icons-material/Cached";
import ApiService from "../../../service/ApiService";
import Pathnames from "../../../model/enum/Pathnames";
import DatabaseEntities from "../../../model/enum/DatabaseEntities";
import PageResultResponse from "../../../model/pageResultResponse";
import Contractor from "../../../model/contractor";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Misc from "../../../utils/Misc";
import useStyles from "../../styles/cardStyles";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: TableHeaderGrey,
    color: PrimaryColor,
    fontWeight: 700,
    whiteSpace: "nowrap",
    zIndex: 0,
    position: "static",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: TableBodyGrey,
    fontFamily: "monospace",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: TableRowHover,
  },
}));

const Contractors = () => {
  const classes = useStyles();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [contractors, setContractors] = useState<Contractor[]>([]);

  // Update the URL with the current page and rowsPerPage
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", page.toString());
    url.searchParams.set("rowsPerPage", rowsPerPage.toString());
    window.history.pushState({}, Pathnames.CONTRACTORS_LIST, url);
  });

  //Get all appointments from the database
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      searchParams.set("page", page.toString());
      searchParams.set("rowsPerPage", rowsPerPage.toString());

      await ApiService.get<PageResultResponse>(
        `${DatabaseEntities.CONTRACTORS}?${searchParams.toString()}`
      ).then((data: PageResultResponse) => {
        if (Misc.isArrayEmpty(data.items)) return;
        const { items, count } = data;
        setContractors(items);
        setTotalPages(count);
      });
      setLoading(false);
    };
    if (loading && Misc.isArrayEmpty(contractors)) fetchData();
  }, [contractors]);

  const resetList = (): void => {
    setPage(1);
    setRowsPerPage(10);
    setContractors([]);
    setLoading(true);
    if (searchParams.has("contractorId")) {
      searchParams.delete("contractorId");
      setSearchParams(searchParams);
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown> | null,
    pg: number
  ) => {
    setPage(pg + 1);
    scrollToTop();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let row = parseInt(event.target.value);
    setRowsPerPage(row);
    scrollToTop();
  };

  const scrollToTop = () => {
    setContractors([]);
    setLoading(true);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const addIdToQueryPath = (id?: number) => {
    if (!id) return;

    const url = new URL(window.location.href);
    url.searchParams.set("contractorId", id.toString());
    window.history.pushState({}, Pathnames.CONTRACTORS_LIST, url);
  };

  const removeIdFromQueryPath = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("contractorId");
    window.history.replaceState({}, "", url.pathname + url.search);
  };

  return (
    <>
      <div className={classes.headerContainer}>
        <Typography className={classes.title}></Typography>
        <div className={classes.headerDiv}>
          <ContractorModal resetList={resetList} />
          <Button onClick={() => resetList()}>
            <CachedIcon />
          </Button>
        </div>
      </div>
      <Paper sx={{ marginTop: "20px" }}>
        <TableContainer>
          <Table
            stickyHeader
            aria-label="customized table"
            padding="normal"
            size="medium"
          >
            <TableHead>
              <TableRow>
                <StyledTableCell align="left">Id</StyledTableCell>
                <StyledTableCell align="left">Name</StyledTableCell>
                <StyledTableCell align="left">Email</StyledTableCell>
                <StyledTableCell align="left">Phone</StyledTableCell>
                <StyledTableCell align="left">Address</StyledTableCell>
                <StyledTableCell align="left">Company Name</StyledTableCell>
                <StyledTableCell align="left">E.I.N</StyledTableCell>
                <StyledTableCell align="left">License Nr</StyledTableCell>
                <StyledTableCell align="left">Insurance Nr</StyledTableCell>
                <StyledTableCell align="left">IsVerified</StyledTableCell>
                <StyledTableCell align="left">Notes</StyledTableCell>
                <StyledTableCell align="left">CreatedAt</StyledTableCell>
                <StyledTableCell align="left">UpdatedAt</StyledTableCell>
                <StyledTableCell align="center">
                  <MoreVertIcon />
                </StyledTableCell>
              </TableRow>
            </TableHead>
            {loading ? (
              <Spinner />
            ) : (
              <TableBody>
                {contractors.length > 0 ? (
                  contractors.map((row: Contractor) => (
                    <StyledTableRow key={row.id}>
                      <StyledTableCell align="right">{row.id}</StyledTableCell>
                      <StyledTableCell align="right">
                        {row.firstName} {row.lastName}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {row.email}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {Misc.formatPhoneNumber(row.phone)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {row.street &&
                          `${row.street}, ${row.city}, ${row.state}, ${row.zip}`}
                      </StyledTableCell> 
                      <StyledTableCell align="right">
                        {row.companyName}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {row.employeerIdentificationNumber}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {row.licenseNumber}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {row.insuranceNumber}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {row.isVerified ? (
                          <CheckCircleOutlineIcon style={{ color: "green" }} />
                        ) : (
                          <ClearIcon style={{ color: "red" }} />
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <span className={classes.limitedText}>{row.notes}</span>
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {DateConverter.toFullDate(row.createdAt)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {DateConverter.toFullDate(row.updatedAt)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <ContractorDetailModal
                          contractor={row}
                          resetList={resetList}
                          onOpen={() => {
                            addIdToQueryPath(row?.id);
                          }}
                          onClose={() => {
                            removeIdFromQueryPath();
                          }}
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <StyledTableCell align="right">No items</StyledTableCell>
                )}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Paper>
      <TablePagination
        component="div"
        count={totalPages}
        page={page - 1}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <ToastContainer />
    </>
  );
};

export default Contractors;
